import React, { useState, useEffect, useRef, useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { BeatLoader } from "react-spinners";
import "../../../../../styles/count_graphs.css";
import {
  barBackgroundColorLineCrossing,
  barBackgroundColorReverseLineCrossing,
} from "../../../../../config/index";
import { BiLineChart, BiBarChartAlt2, BiGitCompare } from "react-icons/bi";
import { MdSwapVert } from "react-icons/md";
import { transformHourlyDataForComparison } from "../baseSuiteUtils";

const HourlyCountChart = (props) => {
  // console.log('HourlyCountChart ALL props:', props);
  // console.log('Props keys:', Object.keys(props));

  const {
    selectedYear,
    selectedMonth,
    selectedDate,
    selectedDayOfWeek,
    hourlyCountData,
    rawHourlyData,
    hourlyDataLoading,
    handleHourlyBarClick,
    chartType = "line",
    onToggleChartType,
    isTrafficView = false,
    setIsTrafficView,
    isCompareMode = false,
    setIsCompareMode,
    processedCameraData = [],
    isFullscreen = false,
  } = props;

  // console.log('HourlyCountChart destructured:', {
  //     selectedYear,
  //     selectedMonth,
  //     selectedDate,
  //     selectedDayOfWeek,
  //     hourlyCountData,
  //     hourlyDataLoading,
  //     dataLength: hourlyCountData?.length,
  //     chartType: chartType,
  //     onToggleChartType: onToggleChartType,
  //     hasToggleFunction: !!onToggleChartType
  // });

  const [chartData, setChartData] = useState();
  const [barChartData, setBarChartData] = useState();
  const [compareChartData, setCompareChartData] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [chartKey, setChartKey] = useState(0); // Key to force chart remount
  // Remove local chartType and isFullscreen state since they're now passed as props

  // Auto-enable traffic view when compare mode is turned on
  useEffect(() => {
    if (isCompareMode && !isTrafficView && setIsTrafficView) {
      setIsTrafficView(true);
    }
  }, [isCompareMode, isTrafficView, setIsTrafficView]);

  // Check if there are multiple cameras available for comparison
  const hasMultipleCameras = useMemo(() => {
    if (!processedCameraData || processedCameraData.length === 0) return false;
    const cameras = processedCameraData.filter(
      (cam) => cam.camera_id !== "All" && cam.camera_id !== null
    );
    return cameras.length > 1;
  }, [processedCameraData]);

  const getMonthName = (monthNumber) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return months[monthNumber - 1];
  };

  const chartRef = useRef(null);

  // Process hourly data - aggregate by hour across all cameras
  const processedHourlyData = useMemo(() => {
    // console.log('Processing hourly data:', hourlyCountData);

    if (!hourlyCountData || hourlyCountData.length === 0) {
      // console.log(' No hourly data to process');
      return [];
    }

    // Group data by hour
    const hourlyGroups = {};

    hourlyCountData.forEach((record) => {
      const hour = record.hour;
      if (!hourlyGroups[hour]) {
        hourlyGroups[hour] = {
          hour: hour,
          hour_display: record.hour_display,
          event_types: {
            "Line Crossing": 0,
            "Reverse Line Crossing": 0,
          },
        };
      }
      hourlyGroups[hour].event_types[record.event_type] +=
        parseInt(record.total_count) || 0;
    });

    // Convert to array and sort by hour
    const processed = Object.values(hourlyGroups).sort(
      (a, b) => a.hour - b.hour
    );
    // console.log('Processed hourly data:', processed);
    return processed;
  }, [hourlyCountData]);

  // Prepare comparison data when in compare mode
  useEffect(() => {
    if (isCompareMode && rawHourlyData && processedCameraData.length > 0) {
      const { cameraDatasets, hourDisplays } = transformHourlyDataForComparison(
        rawHourlyData,
        processedCameraData,
        isTrafficView
      );

      setCompareChartData({
        labels: hourDisplays,
        datasets: cameraDatasets,
      });
    }
  }, [isCompareMode, rawHourlyData, processedCameraData, isTrafficView]);

  // Calculate totals differently based on compare mode
  const { totalPeopleIn, totalPeopleOut } = useMemo(() => {
    if (isCompareMode && rawHourlyData && processedCameraData) {
      // In compare mode: calculate totals from raw data for each camera
      const cameras = processedCameraData.filter(
        (cam) => cam.camera_id !== "All" && cam.camera_id !== null
      );

      let totalIn = 0;
      let totalOut = 0;

      cameras.forEach((camera) => {
        const cameraData = rawHourlyData.filter(
          (d) => d.camera_id === camera.camera_id
        );

        const cameraInSum = cameraData
          .filter((d) => d.event_type === "Line Crossing")
          .reduce((sum, d) => sum + parseInt(d.total_count, 10), 0);

        const cameraOutSum = cameraData
          .filter((d) => d.event_type === "Reverse Line Crossing")
          .reduce((sum, d) => sum + parseInt(d.total_count, 10), 0);

        totalIn += cameraInSum;
        totalOut += cameraOutSum;
      });

      return { totalPeopleIn: totalIn, totalPeopleOut: totalOut };
    } else {
      // Normal mode: sum from processed hourly data
      const totalIn = processedHourlyData.reduce(
        (sum, d) => sum + d.event_types["Line Crossing"],
        0
      );
      const totalOut = processedHourlyData.reduce(
        (sum, d) => sum + d.event_types["Reverse Line Crossing"],
        0
      );
      return { totalPeopleIn: totalIn, totalPeopleOut: totalOut };
    }
  }, [isCompareMode, rawHourlyData, processedCameraData, processedHourlyData]);

  const prepareHourlyCountLineChart = () => {
    if (isTrafficView) {
      return {
        labels: processedHourlyData.map((d) => d.hour_display),
        datasets: [
          {
            label: "Traffic (Average)",
            data: processedHourlyData.map(
              (d) =>
                (d.event_types["Line Crossing"] +
                  d.event_types["Reverse Line Crossing"]) /
                2
            ),
            backgroundColor: "#FFA500",
            borderColor: "#FFA500",
            borderWidth: 3,
            tension: 0.3,
          },
        ],
      };
    } else {
      return {
        labels: processedHourlyData.map((d) => d.hour_display),
        datasets: [
          {
            label: "People In",
            data: processedHourlyData.map(
              (d) => d.event_types["Line Crossing"]
            ),
            backgroundColor: barBackgroundColorLineCrossing,
            borderColor: barBackgroundColorLineCrossing,
            borderWidth: 3,
            tension: 0.3,
          },
          {
            label: "People Out",
            data: processedHourlyData.map(
              (d) => d.event_types["Reverse Line Crossing"]
            ),
            backgroundColor: barBackgroundColorReverseLineCrossing,
            borderColor: barBackgroundColorReverseLineCrossing,
            borderWidth: 3,
            tension: 0.3,
          },
        ],
      };
    }
  };

  const getHourlyLineChartOptions = () => {
    return {
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      hover: {
        mode: "nearest",
        intersect: false,
      },
      onHover: function (event, elements) {
        const chartElement = this.canvas;
        if (elements && elements.length > 0) {
          chartElement.style.cursor = "pointer";
        } else {
          chartElement.style.cursor = "default";
        }
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const hourData = processedHourlyData[index];
          setSelectedHour(hourData.hour);
          if (handleHourlyBarClick) {
            handleHourlyBarClick(hourData);
          }
        }
      },
      plugins: {
        legend: {
          labels: {
            color: "#F8F8F8",
          },
          onHover: (event) => {
            event.native.target.style.cursor = "pointer";
          },
          onLeave: (event) => {
            event.native.target.style.cursor = "default";
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: (context) => {
              const hourData = processedHourlyData[context.index];
              return hourData?.hour === selectedHour ? "#32de84" : "#fff";
            },
            font: {
              weight: (context) => {
                const hourData = processedHourlyData[context.index];
                return hourData?.hour === selectedHour ? "bold" : "normal";
              },
              size: (context) => {
                const hourData = processedHourlyData[context.index];
                return hourData?.hour === selectedHour ? 14 : 12;
              },
            },
            maxRotation: 45,
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          ticks: { font: { size: 12 }, color: "#F8F8F8" },
        },
      },
    };
  };

  const prepareHourlyCountBarChart = () => {
    if (isTrafficView) {
      return {
        labels: processedHourlyData.map((d) => d.hour_display),
        datasets: [
          {
            label: "Traffic (Average)",
            data: processedHourlyData.map(
              (d) =>
                (d.event_types["Line Crossing"] +
                  d.event_types["Reverse Line Crossing"]) /
                2
            ),
            backgroundColor: "#FFA500",
            borderColor: processedHourlyData.map((d) =>
              selectedHour === d.hour ? "#ccc" : "#FF8C00"
            ),
            borderWidth: 1,
          },
        ],
      };
    } else {
      return {
        labels: processedHourlyData.map((d) => d.hour_display),
        datasets: [
          {
            label: "People In",
            data: processedHourlyData.map(
              (d) => d.event_types["Line Crossing"]
            ),
            backgroundColor: barBackgroundColorLineCrossing,
            borderColor: processedHourlyData.map((d) =>
              selectedHour === d.hour ? "#ccc" : "#8E338C"
            ),
            borderWidth: 1,
          },
          {
            label: "People Out",
            data: processedHourlyData.map(
              (d) => d.event_types["Reverse Line Crossing"]
            ),
            backgroundColor: barBackgroundColorReverseLineCrossing,
            borderColor: processedHourlyData.map((d) =>
              selectedHour === d.hour ? "#ccc" : "#8E338C"
            ),
            borderWidth: 1,
          },
        ],
      };
    }
  };

  const getBarChartOptions = () => {
    return {
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      hover: {
        mode: "nearest",
        intersect: false,
      },
      onHover: function (event, elements) {
        const chartElement = this.canvas;
        if (elements && elements.length > 0) {
          chartElement.style.cursor = "pointer";
        } else {
          chartElement.style.cursor = "default";
        }
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const hourData = processedHourlyData[index];
          setSelectedHour(hourData.hour);
          if (handleHourlyBarClick) {
            handleHourlyBarClick(hourData);
          }
        }
      },
      plugins: {
        legend: {
          labels: {
            color: "white",
          },
          onClick: (event, legendItem, legend) => {
            // Enable clicking legend items to toggle camera visibility
            const index = legendItem.datasetIndex;
            const chart = legend.chart;
            const meta = chart.getDatasetMeta(index);

            // Toggle visibility
            meta.hidden =
              meta.hidden === null ? !chart.data.datasets[index].hidden : null;
            chart.update();
          },
          onHover: (event) => {
            event.native.target.style.cursor = "pointer";
          },
          onLeave: (event) => {
            event.native.target.style.cursor = "default";
          },
        },
      },
      scales: {
        x: {
          stacked: !isTrafficView && !isCompareMode,
          grid: { display: false },
          ticks: {
            color: (context) => {
              const hourData = processedHourlyData[context.index];
              return hourData?.hour === selectedHour ? "#32de84" : "#fff";
            },
            font: {
              weight: (context) => {
                const hourData = processedHourlyData[context.index];
                return hourData?.hour === selectedHour ? "bold" : "normal";
              },
              size: (context) => {
                const hourData = processedHourlyData[context.index];
                return hourData?.hour === selectedHour ? 14 : 12;
              },
            },
            maxRotation: 45,
            minRotation: 0,
          },
          barPercentage: 0.5,
          categoryPercentage: 0.5,
        },
        y: {
          stacked: !isTrafficView && !isCompareMode,
          beginAtZero: true,
          ticks: { font: { size: 12 }, color: "#F8F8F8" },
        },
      },
    };
  };

  // Force chart resize when entering/exiting fullscreen
  useEffect(() => {
    const timers = [];

    // Force chart remount when exiting fullscreen to reset sizing
    if (!isFullscreen) {
      setChartKey((prev) => prev + 1);
    }

    // Force immediate DOM reflow
    if (chartRef.current?.canvas) {
      const container = chartRef.current.canvas.parentElement;
      if (container) {
        void container.offsetHeight;
      }
    }

    // First resize immediately
    if (chartRef.current) {
      chartRef.current.resize();
    }

    // Second resize after a short delay
    timers.push(
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.resize();
          chartRef.current.update("none");
        }
      }, 50)
    );

    // Third resize after CSS transition
    timers.push(
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.resize();
          chartRef.current.update("none");
        }
      }, 350)
    );

    // Final resize to ensure everything is settled
    timers.push(
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.resize();
          chartRef.current.update("none");
        }
      }, 500)
    );

    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [isFullscreen]);

  // Handle window resize events in fullscreen mode
  useEffect(() => {
    if (!isFullscreen) return;

    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.resize();
        chartRef.current.update("none");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isFullscreen]);

  // Remove local toggleChartType function since it's now passed as prop

  useEffect(() => {
    // console.log('HourlyCountChart useEffect triggered:', {
    //     processedDataLength: processedHourlyData.length,
    //     chartType,
    //     selectedHour
    // });

    if (processedHourlyData.length > 0) {
      const preparedLineChart = prepareHourlyCountLineChart();
      const preparedBarChart = prepareHourlyCountBarChart();

      // console.log('Preparing chart data:', {
      //     hasLineChart: !!preparedLineChart,
      //     hasBarChart: !!preparedBarChart,
      //     chartType
      // });

      if (preparedLineChart) {
        setChartData(preparedLineChart);
      }
      if (preparedBarChart) {
        setBarChartData(preparedBarChart);
      }
    }
  }, [
    processedHourlyData,
    selectedHour,
    chartType,
    isTrafficView,
    isFullscreen,
  ]); // Include isTrafficView and isFullscreen in dependency array

  Chart.register(...registerables);

  return (
    <div style={{ width: "100%" }}>
      {/* {// console.log('Rendering HourlyCountChart:', {
                processedDataLength: processedHourlyData.length,
                loading: hourlyDataLoading,
                hasData: processedHourlyData.length > 0,
                rawDataLength: hourlyCountData?.length
            })} */}

      {hourlyDataLoading ? (
        <div
          className="loading-container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2rem",
          }}
        >
          <div style={{ color: "white", marginRight: "1rem" }}>
            Loading Hourly Data...
          </div>
          <BeatLoader color={"white"} loading={hourlyDataLoading} size={15} />
        </div>
      ) : processedHourlyData.length > 0 ? (
        <>
          <div className="chart-total-container">
            <div className="chart-total-pill">
              {isTrafficView ? (
                <>
                  <div
                    className="chart-total-label"
                    style={{ color: "#FFA500" }}
                  >
                    Total Traffic (Average):{" "}
                    {Math.round((totalPeopleIn + totalPeopleOut) / 2)}
                  </div>
                  <div className="chart-total-label" style={{ color: "#fff" }}>
                    Date: {getMonthName(selectedMonth)} {selectedDate},{" "}
                    {selectedYear}
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="chart-total-label"
                    style={{ color: "#64C6E6" }}
                  >
                    Total In: {totalPeopleIn}
                  </div>
                  <div
                    className="chart-total-label"
                    style={{ color: "#8E338C" }}
                  >
                    Total Out: {totalPeopleOut}
                  </div>
                  <div className="chart-total-label" style={{ color: "#fff" }}>
                    Date: {getMonthName(selectedMonth)} {selectedDate},{" "}
                    {selectedYear}
                  </div>
                </>
              )}
            </div>
          </div>

          <div>
            {/* // console.log('About to render chart:', {
                            //     chartType: chartType,
                            //     isLine: chartType === 'line',
                            //     isBar: chartType === 'bar',
                            //     hasChartData: !!chartData,
                            //     hasBarChartData: !!barChartData
                            // })} */}
            {chartType === "line" ? (
              <>
                {/* // console.log('Rendering LINE chart') */}
                <Line
                  key={`hourly-line-${chartType}-${chartKey}`}
                  ref={chartRef}
                  data={
                    isCompareMode && compareChartData
                      ? compareChartData
                      : chartData || { labels: [], datasets: [] }
                  }
                  options={getHourlyLineChartOptions()}
                />
              </>
            ) : (
              <>
                {/* // console.log('Rendering BAR chart') */}
                <Bar
                  key={`hourly-bar-${chartType}-${chartKey}`}
                  ref={chartRef}
                  data={
                    isCompareMode && compareChartData
                      ? compareChartData
                      : barChartData || { labels: [], datasets: [] }
                  }
                  options={getBarChartOptions()}
                />
              </>
            )}
          </div>
        </>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "#666",
          }}
        >
          {selectedDate && selectedDayOfWeek
            ? `No hourly data available for ${selectedDayOfWeek} ${selectedDate}`
            : "Select a day to view hourly data"}
          <div style={{ fontSize: "0.9rem", marginTop: "0.5rem" }}>
            Raw data length: {hourlyCountData?.length || 0}
          </div>
        </div>
      )}
    </div>
  );
};

export default HourlyCountChart;
